// See Documentation: ../../../Document/InsightsMap.md
import React, { useRef, useEffect, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import * as turf from "@turf/turf";
import { LngLatBounds, PointLike } from "mapbox-gl";
import {
    Accordion,
    AccordionTrigger,
    AccordionContent,
    AccordionItem,
} from "../ui/Accordion";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "../ui/DropdownMenu";
import SearchIcon from "../../media/SearchIcon";
import MapStyleIcon from "../../media/MapStyleIcon";
import ZoomInIcon from "../../media/ZoomInIcon";
import ZoomOutIcon from "../../media/ZoomOutIcon";
import {
    MAP_STYLES,
    MapViewOptions,
    INITIAL_MAP_POSITION,
    MapStyle,
    THERMAL_CAPACITY_THRESHOLDS,
} from "../constants/mapConstants";
import { createTriangleIcon } from "../../utils/map-utils";
import { SubFilterItems } from "../filters/SubFilterItems";
import {
    MouseEvent,
    IVisibleFeatureType,
    VisibleFeatureType,
    VisibleFeature,
    FeatureGeometry,
} from "../../types/types";
import Layer, { CheckableLayer, FilterCategory } from "../../types/Layer";
import MapToolsMenu from "./tools/selection/MapToolsMenu";
import MapSelectionTools from "./tools/selection/MapSelectionTools";
import MapViewsMenu from "./tools/MapAnalyticsMenu";
import { MapSource } from "../hooks/useMapboxSources";
import MapTooltip from "./MapTooltip";

export interface InsightsMapConfig {
    mapLayerConfig: Layer[];
    mapLayerIds: Map<string, IVisibleFeatureType>; // array of layer ids mapped to a visibility feature
    mouseHandlers: MouseEvent[];
}

interface ViewToggleConfig {
    viewCharts: boolean;
    viewTable: boolean;
    onViewChartsChange: (value: boolean) => void;
    onViewTableChange: (value: boolean) => void;
}

type InsightsMapProps = {
    mapboxAccessToken: string;
    theme: "light" | "dark";
    isPageExpanded: boolean;
    viewToggleConfig?: ViewToggleConfig;
    updateVisibleFeatures: (features: VisibleFeature[]) => void; // some react callback
    updateSelectedArea: (
        bounds: [[number, number], [number, number]] | null,
        features: VisibleFeature[],
        geometry?: GeoJSON.Feature | null,
        mode?: "Rectangle" | "Circle" | "Lasso" | null
    ) => void; // some react callback
    clearSelectedArea: () => void; // some react callback
    mapboxSource: MapSource;
    config: InsightsMapConfig;
    onMapReady?: (map: mapboxgl.Map) => void;
};

const InsightsMap: React.FC<InsightsMapProps> = ({
    mapboxAccessToken,
    theme,
    isPageExpanded,
    viewToggleConfig,
    updateVisibleFeatures,
    updateSelectedArea,
    clearSelectedArea,
    mapboxSource,
    config,
    onMapReady,
}) => {
    mapboxgl.accessToken = mapboxAccessToken;
    const mapContainer = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const initializeRef = useRef(false);
    const [currentMapStyle, setCurrentMapStyle] = useState<MapStyle>("Street");
    // persist the map's zoom level and position in MapViewOptions format
    const [mapPosition, setMapPosition] =
        useState<MapViewOptions>(INITIAL_MAP_POSITION);
    const [layerStates, setLayerStates] = useState<Layer[]>(
        () => config.mapLayerConfig
    );

    // Stop infinite loading
    const mapLayerIds = useRef<Map<string, IVisibleFeatureType>>(
        config.mapLayerIds
    );

    // References of states to prevent cyclical rendering
    const mapPositionRef = useRef(mapPosition);
    const layerStatesRef = useRef(layerStates);

    type SelectionTool = "Rectangle" | "Lasso" | "Circle" | null;
    type CustomSelectionMode = "within" | "contained";

    const [activeTool, setActiveTool] = useState<SelectionTool>(null);
    const [selectionMode, setSelectionMode] =
        useState<CustomSelectionMode>("within");
    const [activeTooltip, setActiveTooltip] = useState<any | null>(null);
    const [activeClickedTooltip, setActiveClickedTooltip] = useState<
        any | null
    >(null);

    // update mapPosRef everytime mapPos is updated
    useEffect(() => {
        mapPositionRef.current = mapPosition;
    }, [mapPosition]);

    const handleStyleChange = (style: MapStyle) => {
        if (!mapRef.current) return;
        if (
            mapRef.current.getStyle()?.sprite!.split("/").at(-1) ===
            MAP_STYLES[style].split("/").at(-1)
        )
            return;

        // Fix runtime errors from activeTool being selected when changing styles
        setActiveTool(null);
        
        // Listen for style load event to clear selection after style loads
        mapRef.current.once('style.load', () => {
            clearSelectedArea();
            collectVisibleFeatures();
        });

        mapRef.current.setStyle(MAP_STYLES[style]);
        setCurrentMapStyle(style);
    };

    const handleZoomIn = () => {
        if (!mapRef.current) return;
        const currentZoom = mapRef.current.getZoom();
        mapRef.current.zoomTo(currentZoom + 0.5, {
            duration: 300,
        });
        setMapPosition({
            zoom: currentZoom + 0.5,
            center: mapRef.current.getCenter().toArray(),
        });
    };

    const handleZoomOut = () => {
        if (!mapRef.current) return;
        const currentZoom = mapRef.current.getZoom();
        mapRef.current.zoomTo(currentZoom - 0.5, {
            duration: 300,
        });
        setMapPosition({
            zoom: currentZoom - 0.5,
            center: mapRef.current.getCenter().toArray(),
        });
    };

    const handleToolChange = (tool: SelectionTool) => {
        // If deactivating tool or changing tools, reset data
        if (tool === null || tool !== activeTool) {
            // Reset selected area and clear selection
            clearSelectedArea();
            collectVisibleFeatures();
            updateSelectionGeometry(
                mapRef.current!,
                turf.feature({
                    type: "Polygon",
                    coordinates: [[]],
                })
            );
        }
        setActiveTool(tool);
    };

    const updateSelectionGeometry = (
        map: mapboxgl.Map,
        geometry: GeoJSON.Feature
    ) => {
        const source = map.getSource(
            "selection-source"
        ) as mapboxgl.GeoJSONSource;
        if (source) {
            source.setData(geometry);
        }
    };

    const toggleLayer = (layerId: string) => {
        setLayerStates((prev) => {
            if (mapRef.current) {
                prev.forEach((layer) => {
                    if (layer.isCheckable) {
                        (layer as CheckableLayer).toggleById(layerId);
                    }
                });
            }
            return prev;
        });
        collectVisibleFeatures();
        updateLayerVisibility();
    };

    const addMapLayers = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;

        if (!map.hasImage("custom-triangle")) {
            const triangleImage = createTriangleIcon();
            if (triangleImage) {
                map.addImage("custom-triangle", triangleImage, {
                    sdf: true,
                }); // sdf: true is important for coloring
            }
        }

        const addLayersDown = (layer: CheckableLayer) => {
            if (layer.layerSettings) {
                const layerId = layer.id;
                const sourceId = layer.layerSettings.source;
                const settings = layer.layerSettings;
                const mapSource = mapboxSource.collectSourceById(sourceId);

                if (!map.getSource(sourceId)) {
                    map.addSource(sourceId, {
                        type: mapSource.type as any,
                        url: mapSource.url,
                    });
                }
                map.addLayer({
                    id: layerId,
                    "source-layer": mapSource.name,
                    ...settings,
                });
            }

            if (layer.subFilters) {
                layer.subFilters.forEach((subLayer) => {
                    addLayersDown(subLayer);
                });
            }
        };

        layerStates.forEach((layer) => {
            addLayersDown(layer as CheckableLayer);
        });
    }, [layerStates, mapboxSource]);

    const collectVisibleFeatures = useCallback(() => {
        if (!mapRef.current) return;

        const map = mapRef.current;
        const bounds = map.getBounds() as LngLatBounds;
        const ne = map.project(bounds.getNorthEast());
        const sw = map.project(bounds.getSouthWest());

        const bbox: [PointLike, PointLike] = [sw, ne];
        const visibleFeatures: VisibleFeature[] = [];

        // Helper function to process features
        const processFeatures = (
            features: mapboxgl.GeoJSONFeature[],
            type: VisibleFeature["type"]
        ): VisibleFeature[] => {
            return features
                .filter((feature) => feature.geometry) // Ensure we have geometry
                .map((feature) => ({
                    id: feature.id?.toString() || `${type}-${Math.random()}`,
                    type,
                    properties: feature.properties || {},
                    geometry: feature.geometry as FeatureGeometry,
                }));
        };

        const collectDown = (layer: CheckableLayer) => {
            if (map.getLayer(layer.id)) {
                const features = map.queryRenderedFeatures(bbox, {
                    layers: [layer.id],
                });
                visibleFeatures.push(
                    ...processFeatures(
                        features,
                        mapLayerIds.current.get(layer.id) ||
                            VisibleFeatureType.other
                    )
                );
            }
            if (layer.subFilters) {
                layer.subFilters.forEach((filter) => collectDown(filter));
            }
        };

        const layerStates = layerStatesRef.current;
        layerStates.forEach((layer) => {
            collectDown(layer as CheckableLayer);
        });

        updateVisibleFeatures(visibleFeatures);
    }, [updateVisibleFeatures, mapLayerIds]);

    // Helper function for updating layer visibility
    const updateLayerVisibility = useCallback(() => {
        if (!mapRef.current) return;
        const map = mapRef.current!;
        const layerStates = layerStatesRef.current;
        const updateVisibilityDown = (layer: CheckableLayer) => {
            const { id, visible, subFilters } = layer;
            // base case
            if (!subFilters) {
                if (!!map.getLayer(id)) {
                    map.setLayoutProperty(
                        id,
                        "visibility",
                        visible ? "visible" : "none"
                    );
                }
            } else {
                // Generalized handling filter expression generation
                if ((layer as FilterCategory).generateFilterExpression) {
                    const generator = (layer as FilterCategory)
                        .generateFilterExpression!;
                    // children to be passed into a filterExpressionGenerator
                    const visibleChildFilters = subFilters.filter(
                        (filter) => filter.visible && !filter.subFilters
                    );
                    const generatedFilterExpression = generator(
                        visibleChildFilters,
                        (layer as FilterCategory).filterProperty
                    );
                    let filterExpression = generatedFilterExpression;
                    if (layer.layerSettings?.filter) {
                        filterExpression = [
                            "all",
                            layer.layerSettings.filter,
                            generatedFilterExpression,
                        ];
                    }

                    map.setFilter(id, filterExpression);
                }
                subFilters.forEach((filter) => {
                    updateVisibilityDown(filter);
                });
            }
        };
        layerStates.forEach((layer) => {
            if (layer.isCheckable)
                updateVisibilityDown(layer as CheckableLayer);
        });
    }, [layerStatesRef, mapRef]);

    // update layerStatesRef everytime layerStates is updated
    useEffect(() => {
        if (layerStates) {
            layerStatesRef.current = layerStates;
            if (mapRef.current) {
                updateLayerVisibility();
            }
        }
    }, [layerStates, updateLayerVisibility]);

    // Create initial map
    useEffect(() => {
        if (initializeRef.current) return;
        initializeRef.current = true;

        if (!mapContainer.current) return;

        const map = new mapboxgl.Map({
            container: mapContainer.current,
            style: MAP_STYLES[currentMapStyle],
            center: INITIAL_MAP_POSITION.center,
            zoom: INITIAL_MAP_POSITION.zoom,
            minZoom: 1,
            maxZoom: 18,
        });

        mapRef.current = map;

        // A style change unmounts and re-initializes the map so we only need to listen for this event once
        map.once("style.load", () => {
            map.setZoom(mapPositionRef.current.zoom);
            map.setCenter(mapPositionRef.current.center);
            addMapLayers();
            updateLayerVisibility();
            map.resize();
            if (onMapReady) onMapReady(map);
        });

        map.once("load", () => {
            collectVisibleFeatures();
        });

        map.on("zoom", () => {
            setMapPosition({
                zoom: map.getZoom(),
                center: map.getCenter().toArray(),
            });
        });

        map.on("moveend", () => {
            setMapPosition({
                zoom: map.getZoom(),
                center: map.getCenter().toArray(),
            });
        });

        // Clean up on unmount
        return () => {
            map.remove();
            mapRef.current = null;
            initializeRef.current = false;
        };
    }, [
        collectVisibleFeatures,
        currentMapStyle,
        updateLayerVisibility,
        addMapLayers,
        onMapReady,
    ]);

    // useeffect for mouse events
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
        });

        const leaveHandler = () => {
            if (!activeTool) {
                map.getCanvas().style.cursor = "";
            }
            setActiveTooltip(null);
        };

        config.mouseHandlers.forEach((handler: MouseEvent) => {
            const wrapFun = (e: mapboxgl.MapMouseEvent) => {
                if (!activeTool) {
                    if (handler.type === "click") {
                        handler.callback(e, map, setActiveClickedTooltip);
                    } else {
                        handler.callback(e, map, setActiveTooltip);
                    }
                }
            };
            map.on(handler.type as any, handler.id, wrapFun);
            if (handler.type === "mouseenter") {
                map.on("mouseleave", handler.id, leaveHandler);
            }
        });

        // Cleanup
        return () => {
            config.mouseHandlers.forEach((handler: MouseEvent) => {
                const wrapFun = (e: mapboxgl.MapMouseEvent) => {
                    if (!activeTool) {
                        if (handler.type === "click") {
                            handler.callback(e, map, setActiveClickedTooltip);
                        } else {
                            handler.callback(e, map, setActiveTooltip);
                        }
                    }
                };
                map.off(handler.type as any, handler.id, wrapFun);
                if (handler.type === "mouseenter") {
                    map.off("mouseleave", handler.id, leaveHandler);
                }
            });
            popup.remove();
        };
    }, [
        activeTool,
        currentMapStyle,
        config.mouseHandlers,
        setActiveTooltip,
        setActiveClickedTooltip,
    ]);

    useEffect(() => {
        if (mapRef.current) {
            setTimeout(() => {
                mapRef.current?.resize();
            }, 300);
        }
    }, [isPageExpanded]);

    // Resizes map on window resize
    useEffect(() => {
        const handleWindowResize = () => {
            setTimeout(() => {
                mapRef.current?.resize();
            }, 300);
        };
        window.addEventListener("resize", handleWindowResize);
        return () => {
            window.removeEventListener("resize", handleWindowResize);
        };
    }, []);

    // Resize map when view controls change
    useEffect(() => {
        if (viewToggleConfig) {
            setTimeout(() => {
                mapRef.current?.resize();
            }, 300);
        }
    }, [viewToggleConfig]);

    return (
        <div
            className="relative rounded-[4px]"
            ref={mapContainer}
            style={{
                height: "100%",
                width: "100%",
            }}
        >
            <div className="absolute z-10 top-0 mt-2 ml-2 flex flex-col gap-2">
                <div className="px-[8px] pt-[6px] pb-[8px] w-[380px] rounded-[4px] bg-white dark:bg-[#2D3748] shadow-md flex flex-col gap-1">
                    <div className="flex flex-row gap-1 items-center justify-between">
                        <div className="relative flex flex-row w-full">
                            <input
                                type="text"
                                placeholder="Substation, Pnode, Queue..."
                                className="text-[#535353] dark:text-[#7A8690] text-[13px] px-2 focus:outline-none dark:bg-[#2D3748] rounded-[3px] border-[1px] border-[#C6C5C5] dark:border-[#7A8690] mt-1 pl-8 w-full h-[30px]"
                            />
                            <div className="absolute left-1 top-[19px] transform -translate-y-1/2">
                                <SearchIcon theme={theme} />
                            </div>
                        </div>
                        <button className="px-[6px] h-[30px] mt-1 rounded-[3px] bg-accent1 hover:bg-accent2 duration-300 text-white text-[13px] font-semibold">
                            Search
                        </button>
                    </div>
                </div>
                <div className="px-[6px] py-[6px] w-[240px] rounded-[4px] bg-white dark:bg-[#2D3748] shadow-md">
                    <Accordion
                        type="multiple"
                        className="max-h-[310px] overflow-y-scroll"
                    >
                        {layerStates
                            .filter((layer) => layer.isCheckable)
                            .map((layer) => layer as CheckableLayer)
                            .map((layer: CheckableLayer) => (
                                <AccordionItem
                                    key={layer.id}
                                    value={layer.label}
                                >
                                    <AccordionTrigger
                                        className="px-2 py-1 m-0 hover:bg-[#C6C5C5]] dark:hover:bg-gray-700 rounded-[4px] duration-300"
                                        checkboxProps={{
                                            checked: layer.visible,
                                            onCheckedChange: () => {
                                                toggleLayer(layer.id);
                                            },
                                            disabled: false,
                                        }}
                                    >
                                        {layer.label}
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <SubFilterItems
                                            subFilters={layer.subFilters ?? []}
                                            toggleSubFilter={toggleLayer}
                                            layerId={layer.id}
                                        />
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                    </Accordion>
                </div>
            </div>
            <div className="absolute z-50 top-2 right-2 flex flex-col gap-2">
                <DropdownMenu>
                    <MapTooltip
                        button={
                            <DropdownMenuTrigger asChild>
                                <div className="bg-white dark:bg-[#2D3748] w-[24px] h-[24px] hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] rounded-[4px] shadow-md flex items-center justify-center cursor-pointer focus:ring-none duration-200">
                                    <MapStyleIcon theme={theme} />
                                </div>
                            </DropdownMenuTrigger>
                        }
                        text="Select map style"
                    />
                    <DropdownMenuContent align="start" side="left">
                        {(Object.keys(MAP_STYLES) as MapStyle[]).map(
                            (style) => (
                                <DropdownMenuItem
                                    key={style}
                                    onClick={() => handleStyleChange(style)}
                                    className={`cursor-pointer ${
                                        currentMapStyle === style &&
                                        "bg-gray-300 dark:bg-gray-800 text-black dark:text-white"
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        {style}
                                    </div>
                                </DropdownMenuItem>
                            )
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
                {mapRef.current && (
                    <MapSelectionTools
                        map={mapRef.current}
                        activeTool={activeTool}
                        selectionMode={selectionMode}
                        onSelectionComplete={({ bounds, features, geometry, }) => {
                            updateSelectedArea(bounds, features, geometry, activeTool);
                            updateVisibleFeatures(features);
                        }}
                        onRequestToolReset={() => {
                            setActiveTool(null);
                            clearSelectedArea();
                        }}
                        mapLayerIds={mapLayerIds.current}
                    />
                )}
                <MapToolsMenu
                    activeTool={activeTool}
                    selectionMode={selectionMode}
                    onToolChange={handleToolChange}
                    onModeChange={setSelectionMode}
                />
                {viewToggleConfig && (
                    <MapViewsMenu
                        viewCharts={viewToggleConfig.viewCharts}
                        viewTable={viewToggleConfig.viewTable}
                        onViewChartsChange={viewToggleConfig.onViewChartsChange}
                        onViewTableChange={viewToggleConfig.onViewTableChange}
                    />
                )}
            </div>
            <div className="absolute z-10 bottom-2 right-2 flex flex-col gap-2">
                <div className="flex flex-col gap-[2px] items-end">
                    <MapTooltip
                        button={
                            <button
                                className="bg-white dark:bg-[#2D3748] hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] duration-200 w-[24px] h-[24px] rounded-[4px] shadow-md flex items-center justify-center"
                                onClick={handleZoomIn}
                            >
                                <ZoomInIcon theme={theme} />
                            </button>
                        }
                        text="Zoom in"
                    />
                    <MapTooltip
                        button={
                            <button
                                className="bg-white dark:bg-[#2D3748] hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] duration-200 w-[24px] h-[24px] rounded-[4px] shadow-md flex items-center justify-center"
                                onClick={handleZoomOut}
                            >
                                <ZoomOutIcon theme={theme} />
                            </button>
                        }
                        text="Zoom out"
                    />
                </div>
                <div className="bg-white dark:bg-[#2D3748] w-[65px] rounded-[4px] px-1 py-0.5 shadow-md">
                    <div className="text-[12px] text-black dark:text-white flex justify-center">
                        {`ATC [MW]`}
                    </div>
                    <div className="flex flex-col pl-1">
                        {THERMAL_CAPACITY_THRESHOLDS.map((entry, index) => (
                            <div
                                key={index}
                                className="flex gap-1.5 items-center -my-[2px]"
                            >
                                <div
                                    className={`flex w-[20px] h-[10px]`}
                                    style={{ backgroundColor: entry.color }}
                                />
                                <div className="text-[10px] text-[#535353] dark:text-[#CEE0EF]">
                                    {entry.value}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {activeTooltip}
            {activeClickedTooltip}
        </div>
    );
};
export default InsightsMap;
